/* eslint-disable no-restricted-imports */
import React, {useEffect, useRef, useState} from 'react'
import ReactDOM from 'react-dom'
import styles from './UIGameEnd.module.scss'
import UIGameEndProvider, {useUIGameEndContext} from '../../contexts/UIGameEndContext'
import {Header} from './Header'
import {Content} from './Content'
import {Footer} from './Footer'
import Confetti from 'react-confetti'
import {RankedScreen} from './RankedScreen'
import {Sound} from '../../utils/sound'
import {UIUtilsEjsVariables} from '../../../../../code/client/shared/scripts/UIUtilsEjsVariables'
import {ClientUserState} from '../../../../../code/client/shared/scripts/ClientUserState'
import {ViewPopupEndgameSignup} from '../../../../../code/client/shared/scripts/ui_popup/ViewPopupEndgameSignup'
import {useUIGameManagerContext} from '../../contexts/UIGameManagerContext'
import {GameEndData} from '@colonist/game-base-types'

export const UIGameEnd = ({
    containerRef,
    isAfterLiveGame = false,
}: {
    containerRef: React.Ref<HTMLDivElement> | null
    isAfterLiveGame?: boolean | undefined
}) => (
    ReactDOM.createPortal(<div
        className={styles.gameEndContainer}
        id='ui-game-end-container'
        ref={containerRef}>
        <UIGameEndProviderContainer isAfterLiveGame={isAfterLiveGame}/>
    </div>,
    document.body,
    'ui-game-end-container')
)

// TODO remove once SocketWebActionController is converted to ReactJS
let globalEndGameDataSetter: undefined | ((gameEndData: GameEndData) => void)
export function setGlobalEndGameData(gameEndData: GameEndData) {
    if(globalEndGameDataSetter) globalEndGameDataSetter(gameEndData)
}

export const UIGameEndProviderContainer = ({isAfterLiveGame}: {isAfterLiveGame: boolean}) => {
    const [gameEndData, setGameEndData] = useState<GameEndData | null>(null)
    globalEndGameDataSetter = setGameEndData
    if(!gameEndData) {
        return null
    }
    return <UIGameEndProvider gameEndData={gameEndData} isAfterLiveGame={isAfterLiveGame}>
        <UIGameEndComponent />
    </UIGameEndProvider>
}

export function UIGameEndComponent() {
    const gameManager = useUIGameManagerContext()
    const isReplay = gameManager.gameController.isReplay

    const {isPlayerHasRankedState, isCurrentPlayerWinner} = useUIGameEndContext()
    const [showRankedScreen, setShowRankedScreen] = useState(isPlayerHasRankedState && !isReplay)
    const [runConfetti, setRunConfetti] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const playVictorySound = () => {
        // Original issue: https://github.com/colonistio/katan/pull/9949
        // TODO Eventually replace the victory sound with the end game music and play for all players
        // Sound.playSound(Sound.SoundType.MusicEgoSum)

        if(!isCurrentPlayerWinner) return
        if(isReplay) return

        Sound.playSound(Sound.SoundType.Victory)
    }

    const showSignupPopup = () => {
        if(UIUtilsEjsVariables.isUserOnDiscord) return
        if(UIUtilsEjsVariables.isUserOnMobileApp) return
        if(ClientUserState.userState?.isLoggedIn) return

        const gameCountMilestones = [5, 10, 25]
        const postMilestonesInterval = 50
        const completedGameCount = (ClientUserState.userState?.totalCompletedGameCount ?? 0) + 1
        const isHardCodedGameCount = gameCountMilestones.includes(completedGameCount)
        const passedTheHardcodedGameCounts = completedGameCount > Math.max(...gameCountMilestones)
        const hitMilestoneInterval = completedGameCount % postMilestonesInterval == 0

        const shouldShowSignupPopup = isHardCodedGameCount || (passedTheHardcodedGameCounts && hitMilestoneInterval)
        if(!shouldShowSignupPopup) return

        const popup = new ViewPopupEndgameSignup(completedGameCount)
        popup.show()
    }

    useEffect(() => {
        setRunConfetti(true)
        playVictorySound()
        showSignupPopup()
    }, [])

    const canvas = gameManager.canvasController
    const confettiXPosition = (canvas.appW - canvas.chatWidth) / 2

    return (
        <div
            className={styles.container}
            ref={containerRef}>
            {isCurrentPlayerWinner && !isReplay && runConfetti && ReactDOM.createPortal(<Confetti
                className={styles.confetti}
                confettiSource={{x: confettiXPosition, y: 0, w: 100, h: 30}}
                gravity={0.05}
                initialVelocityX={{min: -10, max: 20}}
                initialVelocityY={-10}
                numberOfPieces={150}
                recycle={false}
                run
            />,
            document.body)}
            <Header disableAnimation={isReplay}/>
            {showRankedScreen ? <RankedScreen
                onContinue={() => {
                    setShowRankedScreen(false)
                }}/> : <>
                <Content disableAnimation={isReplay}/>
                <Footer disableAnimation={isReplay}/>
            </>}
        </div>
    )
}
